"use client";

import React, { useEffect, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function ImageLightbox({
  src,
  alt,
  caption,
  open,
  onClose,
  onPrev,
  onNext,
  label = "Image preview",
}: {
  src?: string | null;
  alt?: string | null;
  caption?: string | null;
  open: boolean;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  label?: string;
}) {
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Arrow navigation
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev?.();
      if (e.key === "ArrowRight") onNext?.();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, onPrev, onNext]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="p-0 bg-transparent border-0 shadow-none outline-none w-full h-full max-w-none md:max-w-none" aria-label={label}>
        {/* Clickable overlay to close */}
        <div className="fixed inset-0 bg-black/85 z-40" onClick={onClose} />
        <div className="fixed inset-0 grid place-items-center pointer-events-none z-50 p-4 md:p-0">
          {src ? (
            <figure className="max-h-[92vh] max-w-[95vw] pointer-events-auto">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img ref={imgRef} src={src} alt={alt || label} className="max-h-[85vh] max-w-full rounded-lg shadow-2xl" />
              {(caption || alt) && (
                <figcaption className="mt-2 text-center text-xs text-white/90">
                  {caption || alt}
                </figcaption>
              )}
            </figure>
          ) : null}

          {/* Close button */}
          <button
            type="button"
            className="absolute top-4 right-4 z-50 rounded-full p-2 grid place-items-center text-white/90 hover:text-white focus-ring pointer-events-auto bg-black/40 hover:bg-black/60 transition-colors"
            aria-label="Close image viewer"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </button>

          {/* Prev/Next */}
          {onPrev && (
            <button
              type="button"
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 rounded-full hit-44 grid place-items-center text-white/85 hover:text-white focus-ring pointer-events-auto"
              aria-label="Previous image"
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
            >
              <ChevronLeft className="icon-24" />
            </button>
          )}
          {onNext && (
            <button
              type="button"
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 rounded-full hit-44 grid place-items-center text-white/85 hover:text-white focus-ring pointer-events-auto"
              aria-label="Next image"
              onClick={(e) => { e.stopPropagation(); onNext(); }}
            >
              <ChevronRight className="icon-24" />
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
